import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import useHttp from '../../../../hooks/http.hook'
import {
  formatVariantDisplayName,
  getGroupDisplayPrice,
  mapBusinessProductGroups,
  normalizeProductImages,
  primaryProductImageUrl,
} from '../../../../utils/productGroups'
import { formatDurationLabel } from '../../../../utils/rentalPeriod'

const FLY_DURATION_MS = 800
const GALLERY_SLIDE_MS = 340

function readTranslateX(el) {
  const transform = getComputedStyle(el).transform
  if (!transform || transform === 'none') return 0
  return new DOMMatrix(transform).m41
}

function getDefaultFabCenter() {
  if (typeof window === 'undefined') return { x: 100, y: 100 }
  const isMobile = window.innerWidth <= 768
  return {
    x: isMobile ? 50 : window.innerWidth - 100,
    y: window.innerHeight - 150,
  }
}

function readFabCenter() {
  const fab = document.querySelector('.floating-action-button')
  if (!fab) return null
  const rect = fab.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return null
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  }
}

async function resolveFlyTarget() {
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))
  let target = readFabCenter()
  if (target) return target
  await new Promise((r) => setTimeout(r, 80))
  target = readFabCenter()
  return target ?? getDefaultFabCenter()
}

function parsePrice(value) {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : NaN
}

function variantHasDiscount(variant) {
  const pid = variant?.promotionId
  if (pid == null || pid === '') return false
  const cur = parsePrice(variant?.price)
  const old = parsePrice(variant?.oldPrice ?? variant?.compareAtPrice)
  return Number.isFinite(cur) && Number.isFinite(old) && old > cur
}

function toCartProduct(group, variant) {
  const cartItem = {
    id: variant.id ?? variant._id,
    _id: variant.id ?? variant._id,
    name: formatVariantDisplayName(group?.name, variant?.title),
    price: variant.price,
    image: group?.image ?? group?.imageUrl ?? primaryProductImageUrl(group?.images),
    description: group?.description || '',
    durationMinutes: variant.durationMinutes ?? null,
  }
  if (variantHasDiscount(variant) && variant?.promotionId != null && variant?.promotionId !== '') {
    cartItem.promotionId = variant.promotionId
    cartItem.oldPrice = variant.oldPrice
  }
  return cartItem
}

/**
 * All state and behavior for a catalog product card: detail modal (fetch,
 * infinite gallery, variants), variant picker, and fly-to-cart animation.
 * Presentational skins (regular, fashion) consume the returned object.
 */
export function useProductCard(product, { onAddToCart, cartCount = 0 } = {}) {
  const { business } = useParams()
  const { requestWithMeta } = useHttp()
  const [isAnimating, setIsAnimating] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailProduct, setDetailProduct] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)
  const [variantPickerOpen, setVariantPickerOpen] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const [dragX, setDragX] = useState(0)
  const [trackAnimating, setTrackAnimating] = useState(false)
  const [selectedVariantId, setSelectedVariantId] = useState(null)
  const galleryRef = useRef(null)
  const dragXRef = useRef(0)
  const imageIndexRef = useRef(0)
  const imageCountRef = useRef(0)
  const pendingSlideRef = useRef(0)
  const slideTimerRef = useRef(null)
  const [flyItems, setFlyItems] = useState([])
  const flyIdRef = useRef(0)
  const pendingFlyOriginRef = useRef(null)

  const productId = product?.id ?? product?._id
  const displayProduct = detailProduct ?? product

  const productImages = useMemo(
    () => normalizeProductImages(product?.images, product?.image ?? product?.imageUrl),
    [product]
  )
  const coverImage = primaryProductImageUrl(productImages, product?.image ?? product?.imageUrl)

  const detailImages = useMemo(
    () =>
      normalizeProductImages(
        displayProduct?.images,
        displayProduct?.image ?? displayProduct?.imageUrl
      ),
    [displayProduct]
  )
  const detailCoverImage = primaryProductImageUrl(
    detailImages,
    displayProduct?.image ?? displayProduct?.imageUrl
  )
  const activeImage = detailImages[imageIndex]?.url ?? detailCoverImage ?? coverImage

  useEffect(() => {
    setImageIndex(0)
  }, [product?.id, product?._id])

  useEffect(() => {
    imageIndexRef.current = imageIndex
  }, [imageIndex])

  useEffect(() => {
    pendingSlideRef.current = 0
    dragXRef.current = 0
    if (slideTimerRef.current) {
      clearTimeout(slideTimerRef.current)
      slideTimerRef.current = null
    }
    setDragX(0)
    setTrackAnimating(false)
    if (!detailOpen) return
    imageIndexRef.current = 0
    setImageIndex(0)
  }, [detailOpen, detailProduct?.id, detailProduct?._id])

  useEffect(() => {
    if (!detailOpen || !business || productId == null) return

    let cancelled = false

    const fetchProductDetails = async () => {
      setDetailLoading(true)
      setDetailError(null)
      try {
        const { ok, data } = await requestWithMeta(
          `/business/${encodeURIComponent(business)}/products/${encodeURIComponent(productId)}`
        )
        if (cancelled) return
        if (!ok || !data?.product) {
          setDetailError(data?.error || 'Не удалось загрузить товар')
          return
        }
        const mapped = mapBusinessProductGroups([data.product])[0]
        setDetailProduct(mapped ?? null)
      } catch (err) {
        if (!cancelled) {
          setDetailError(err?.message || 'Не удалось загрузить товар')
        }
      } finally {
        if (!cancelled) setDetailLoading(false)
      }
    }

    fetchProductDetails()

    return () => {
      cancelled = true
    }
  }, [detailOpen, business, productId, requestWithMeta])

  const closeDetail = useCallback(() => {
    setDetailOpen(false)
    setDetailProduct(null)
    setDetailError(null)
    setDetailLoading(false)
    setSelectedVariantId(null)
  }, [])

  const openDetail = useCallback(() => {
    setDetailOpen(true)
  }, [])

  const variants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product]
  )
  const singleVariant = variants.length === 1 ? variants[0] : null
  const multiVariant = variants.length > 1

  const detailVariants = useMemo(
    () => (Array.isArray(displayProduct?.variants) ? displayProduct.variants : []),
    [displayProduct]
  )
  const detailSingleVariant = detailVariants.length === 1 ? detailVariants[0] : null
  const detailMultiVariant = detailVariants.length > 1
  const selectedDetailVariant = useMemo(() => {
    if (detailSingleVariant) return detailSingleVariant
    return detailVariants.find((v) => (v.id ?? v._id) === selectedVariantId) ?? null
  }, [detailSingleVariant, detailVariants, selectedVariantId])

  useEffect(() => {
    if (!detailOpen || detailVariants.length === 0) return
    setSelectedVariantId((prev) => {
      if (prev != null && detailVariants.some((v) => (v.id ?? v._id) === prev)) return prev
      return detailVariants[0].id ?? detailVariants[0]._id ?? null
    })
  }, [detailOpen, detailVariants])

  const { currentPrice, oldPrice, pricePrefix, priceSuffix } = useMemo(() => {
    const suffixFor = (variant) => {
      const label = formatDurationLabel(variant?.durationMinutes)
      return label ? ` / ${label}` : ''
    }
    if (singleVariant) {
      const current = parsePrice(singleVariant?.price)
      const old = parsePrice(singleVariant?.oldPrice ?? singleVariant?.compareAtPrice)
      const showOld = Number.isFinite(current) && Number.isFinite(old) && old > current
      return {
        currentPrice: current,
        oldPrice: showOld ? old : null,
        pricePrefix: '',
        priceSuffix: suffixFor(singleVariant),
      }
    }
    const min = getGroupDisplayPrice(product)
    return {
      currentPrice: min,
      oldPrice: null,
      pricePrefix: Number.isFinite(min) ? 'от ' : '',
      priceSuffix: '',
    }
  }, [product, singleVariant])

  const detailPricing = useMemo(() => {
    const variant = selectedDetailVariant ?? detailSingleVariant
    if (variant) {
      const current = parsePrice(variant?.price)
      const old = parsePrice(variant?.oldPrice ?? variant?.compareAtPrice)
      const showOld = Number.isFinite(current) && Number.isFinite(old) && old > current
      return {
        currentPrice: current,
        oldPrice: showOld ? old : null,
        pricePrefix: '',
        priceSuffix: formatDurationLabel(variant?.durationMinutes)
          ? ` / ${formatDurationLabel(variant.durationMinutes)}`
          : '',
      }
    }
    const min = getGroupDisplayPrice(displayProduct)
    return {
      currentPrice: min,
      oldPrice: null,
      pricePrefix: Number.isFinite(min) ? 'от ' : '',
      priceSuffix: '',
    }
  }, [displayProduct, detailSingleVariant, selectedDetailVariant])

  useEffect(() => {
    if (!detailOpen && !variantPickerOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closeDetail()
        setVariantPickerOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
    }
  }, [detailOpen, variantPickerOpen, closeDetail])

  useEffect(() => {
    if (!detailOpen) return
    const html = document.documentElement
    const prevHtmlOverflow = html.style.overflow
    const prevBodyOverflow = document.body.style.overflow
    html.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      html.style.overflow = prevHtmlOverflow
      document.body.style.overflow = prevBodyOverflow
    }
  }, [detailOpen])

  const descriptionText = (displayProduct?.description ?? '').trim()
  const detailTitleId = `product-detail-title-${String(product?.id ?? product?._id ?? 'item')}`

  const spawnFlyToCart = useCallback(async (fromX, fromY) => {
    let target = await resolveFlyTarget()
    if (Math.hypot(target.x - fromX, target.y - fromY) < 48) {
      target = getDefaultFabCenter()
    }
    const id = ++flyIdRef.current
    const dx = target.x - fromX
    const dy = target.y - fromY

    setFlyItems((prev) => [
      ...prev,
      { id, fromX, fromY, dx, dy, image: coverImage },
    ])

    window.setTimeout(() => {
      setFlyItems((prev) => prev.filter((item) => item.id !== id))
      window.dispatchEvent(new CustomEvent('shops:cart-fly-landed'))
    }, FLY_DURATION_MS)
  }, [coverImage])

  const commitAdd = useCallback((cartProduct, origin) => {
    setIsAnimating(true)
    const isFirstCartAdd = cartCount === 0
    if (!isFirstCartAdd && origin) {
      spawnFlyToCart(origin.x, origin.y)
    }
    if (onAddToCart) {
      onAddToCart(cartProduct, origin)
    }
    window.setTimeout(() => {
      setIsAnimating(false)
    }, FLY_DURATION_MS)
  }, [cartCount, onAddToCart, spawnFlyToCart])

  const handleAddClick = (e) => {
    e.stopPropagation()
    const buttonRect = e.currentTarget.getBoundingClientRect()
    const origin = {
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2,
    }

    if (multiVariant) {
      pendingFlyOriginRef.current = origin
      setVariantPickerOpen(true)
      return
    }

    if (!singleVariant) return
    commitAdd(toCartProduct(product, singleVariant), origin)
  }

  const handlePickVariant = (variant) => {
    const origin = pendingFlyOriginRef.current
    setVariantPickerOpen(false)
    pendingFlyOriginRef.current = null
    commitAdd(toCartProduct(product, variant), origin)
  }

  const handleDetailAddToCart = (e) => {
    if (!selectedDetailVariant) return
    const buttonRect = e.currentTarget.getBoundingClientRect()
    const origin = {
      x: buttonRect.left + buttonRect.width / 2,
      y: buttonRect.top + buttonRect.height / 2,
    }
    closeDetail()
    commitAdd(toCartProduct(displayProduct, selectedDetailVariant), origin)
  }

  const handleDetailVariantClick = (variant) => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      closeDetail()
      commitAdd(toCartProduct(displayProduct, variant), null)
      return
    }
    setSelectedVariantId(variant.id ?? variant._id)
  }

  imageCountRef.current = detailImages.length
  const showGalleryControls = detailImages.length > 1
  const safeImageIndex = showGalleryControls
    ? ((imageIndex % detailImages.length) + detailImages.length) % detailImages.length
    : 0
  const loopSlides = showGalleryControls
    ? [detailImages[detailImages.length - 1], ...detailImages, detailImages[0]]
    : []

  const clearSlideTimer = useCallback(() => {
    if (!slideTimerRef.current) return
    clearTimeout(slideTimerRef.current)
    slideTimerRef.current = null
  }, [])

  const commitSlide = useCallback((dir) => {
    if (!dir || pendingSlideRef.current !== dir) return
    pendingSlideRef.current = 0
    clearSlideTimer()
    const len = imageCountRef.current
    const next = len > 1 ? (imageIndexRef.current + dir + len) % len : 0
    imageIndexRef.current = next
    dragXRef.current = 0
    setDragX(0)
    setTrackAnimating(false)
    setImageIndex(next)
  }, [clearSlideTimer])

  const beginSlide = useCallback((dir, fromDrag = 0) => {
    const len = imageCountRef.current
    if (!dir || len <= 1 || pendingSlideRef.current) return
    const width = galleryRef.current?.clientWidth ?? 0
    if (width <= 0) {
      const next = (imageIndexRef.current + dir + len) % len
      imageIndexRef.current = next
      setImageIndex(next)
      return
    }
    const target = dir > 0 ? -width : width
    if (Math.abs(fromDrag - target) < 1.5) {
      pendingSlideRef.current = dir
      commitSlide(dir)
      return
    }
    pendingSlideRef.current = dir
    dragXRef.current = target
    setDragX(target)
    setTrackAnimating(true)
    clearSlideTimer()
    slideTimerRef.current = setTimeout(() => commitSlide(dir), GALLERY_SLIDE_MS + 80)
  }, [clearSlideTimer, commitSlide])

  const settleSlide = useCallback((drag, velocity) => {
    const width = galleryRef.current?.clientWidth ?? 0
    if (width <= 0) {
      dragXRef.current = 0
      setDragX(0)
      setTrackAnimating(false)
      return
    }
    const threshold = Math.min(width * 0.2, 64)
    let dir = 0
    if (drag <= -threshold || (velocity < -0.45 && drag < -12)) dir = 1
    else if (drag >= threshold || (velocity > 0.45 && drag > 12)) dir = -1
    if (!dir) {
      pendingSlideRef.current = 0
      clearSlideTimer()
      dragXRef.current = 0
      setDragX(0)
      setTrackAnimating(Math.abs(drag) > 0.5)
      return
    }
    beginSlide(dir, drag)
  }, [beginSlide, clearSlideTimer])

  const stepImage = (delta, e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    beginSlide(delta > 0 ? 1 : -1, 0)
  }

  const handleTrackTransitionEnd = (e) => {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return
    const dir = pendingSlideRef.current
    if (!dir) {
      setTrackAnimating(false)
      return
    }
    commitSlide(dir)
  }

  const selectDetailImage = (index) => {
    pendingSlideRef.current = 0
    clearSlideTimer()
    dragXRef.current = 0
    imageIndexRef.current = index
    setDragX(0)
    setTrackAnimating(false)
    setImageIndex(index)
  }

  useEffect(() => {
    const el = galleryRef.current
    if (!detailOpen || detailLoading || !showGalleryControls || !el) return

    const gesture = {
      tracking: false,
      locked: null,
      startX: 0,
      startY: 0,
      startT: 0,
      lastX: 0,
      lastT: 0,
      originDrag: 0,
      width: 0,
    }

    const onStart = (e) => {
      if (e.touches.length !== 1) return
      if (e.target.closest('button')) return
      const track = el.querySelector('.product-detail-image-track')
      if (!track) return
      const width = track.clientWidth || el.clientWidth
      const len = imageCountRef.current || 1
      const index = ((imageIndexRef.current % len) + len) % len
      const base = -(index + 1) * width
      let drag = readTranslateX(track) - base
      if (drag > width) drag = width
      if (drag < -width) drag = -width
      pendingSlideRef.current = 0
      clearSlideTimer()
      dragXRef.current = drag
      setDragX(drag)
      setTrackAnimating(false)

      const touch = e.touches[0]
      gesture.tracking = true
      gesture.locked = null
      gesture.startX = touch.clientX
      gesture.startY = touch.clientY
      gesture.startT = e.timeStamp
      gesture.lastX = touch.clientX
      gesture.lastT = e.timeStamp
      gesture.originDrag = drag
      gesture.width = width
    }

    const onMove = (e) => {
      if (!gesture.tracking) return
      const touch = e.touches[0]
      if (!touch) return
      const dx = touch.clientX - gesture.startX
      const dy = touch.clientY - gesture.startY
      if (!gesture.locked) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
        gesture.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y'
      }
      if (gesture.locked !== 'x') return
      if (e.cancelable) e.preventDefault()
      const width = gesture.width || el.clientWidth
      const next = Math.max(-width, Math.min(width, gesture.originDrag + dx))
      dragXRef.current = next
      setDragX(next)
      gesture.lastX = touch.clientX
      gesture.lastT = e.timeStamp
    }

    const finish = (e) => {
      if (!gesture.tracking) return
      gesture.tracking = false
      const touch = e.changedTouches?.[0]
      let velocity = 0
      if (touch && gesture.locked === 'x') {
        const dt = Math.max(e.timeStamp - gesture.lastT, 1)
        const instant = dt <= 48 ? (touch.clientX - gesture.lastX) / dt : 0
        const elapsed = Math.max(e.timeStamp - gesture.startT, 1)
        const overall = (touch.clientX - gesture.startX) / elapsed
        velocity = Math.abs(instant) > Math.abs(overall) ? instant : overall
      }
      settleSlide(dragXRef.current, gesture.locked === 'y' ? 0 : velocity)
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', finish)
    el.addEventListener('touchcancel', finish)

    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', finish)
      el.removeEventListener('touchcancel', finish)
    }
  }, [detailOpen, detailLoading, showGalleryControls, clearSlideTimer, settleSlide])

  return {
    product,
    productImages,
    coverImage,
    currentPrice,
    oldPrice,
    pricePrefix,
    priceSuffix,
    isAnimating,
    handleAddClick,
    detailOpen,
    openDetail,
    closeDetail,
    detailLoading,
    detailProduct,
    detailError,
    detailTitleId,
    galleryRef,
    showGalleryControls,
    trackAnimating,
    safeImageIndex,
    dragX,
    handleTrackTransitionEnd,
    loopSlides,
    activeImage,
    stepImage,
    detailImages,
    imageIndex,
    selectDetailImage,
    displayProduct,
    detailPricing,
    descriptionText,
    detailMultiVariant,
    detailVariants,
    selectedVariantId,
    handleDetailVariantClick,
    selectedDetailVariant,
    handleDetailAddToCart,
    variantPickerOpen,
    setVariantPickerOpen,
    variants,
    handlePickVariant,
    flyItems,
  }
}
